import React from "react";
import clsx from "clsx";
import _ from "lodash";
import { connect } from 'react-redux';

import withStyles from "@material-ui/core/styles/withStyles";
import Grid from "@material-ui/core/Grid";
import {ArrowBack} from "@material-ui/icons";

import PrimaryButton from "../../../../../../../components/Buttons/PrimaryButton";
import StepFooter from "../../StepFooter/StepFooter";
import {
  ASSET_DATA_UID, LIABILITIES_V2_STEP_UID,
  LIABILITY_CHECKBOX_UID,
  LIABILITY_DATA_UID,
  SAVE_AND_CONTINUE_BTN_LABEL
} from "../../../../../constants";
import AssetsList from "../../../../../../OtherAssetV2/AssetsList";
import LiabilitiesList from "../../../../../../OtherAssetV2/LiabilitiesList";
import {getUID} from "../../../../../utils";
import Summary from "../../../../../../OtherAssetV2/Summary";
import CheckboxCardQuestion from "../../question/ChecboxCardQuestion";
import {displayErrorSnackBar} from "../../../../../../../components/SnackbarProvider/actions";
import {GROUPED_ASSET_AND_LIABILITY_CATEGORIES} from "../../../../../../OtherAssetV2/constants";
import {decimalStrToNumber, getErrorMessage} from "../../../../../../../utils/utils";
import BcaIconButton from "../../../../../../../components/Buttons/BcaIconButton/BcaIconButton";
import useOtherAssetsAndLiabilitiesData from "../../../../../../OtherAssetV2/hooks/useOtherAssetsAndLiabilitiesData";
import useDepotsAndCashData from "../../../../../../OtherAssetV2/hooks/useDepotsAndCashData";

import styles from "../styles";


const ASSETS_STEP_UID = 'A7';
const SUMMARY_STEP_UID = 'A72';

const getAsset = (a, categoryId) => ({
  name: a.name,
  sub_category: a.sub_category_display,
  value: decimalStrToNumber(a.asset_value),
  category: GROUPED_ASSET_AND_LIABILITY_CATEGORIES[categoryId].title
});

const getActiveAssets = (categoryId, category) => category.assets.filter(a => !a.is_expired).map(a => getAsset(a, categoryId));

const getLiabilityCheckboxRequired = (checkbox, newDesign) => {
  return newDesign ? checkbox.required_check : !checkbox.optional;
};

const AssetsV2Step = props => {
  const {
    dispatch,
    classes,
    uid,
    name,
    questions,
    help_text,
    extra_texts,
    onPrevClick,
    onNextClick,
    onAnswerChange,
    extraRightButtons,
    dataService,
  } = props;

  const [data, fetch, dispatchAssetsAndLiabilities] = useOtherAssetsAndLiabilitiesData(dataService.customer_id, undefined, undefined, true, true, false);
  const [depotsAndCashData, fetchDepotsAndCash, removeDepot] = useDepotsAndCashData(dataService.customer_id, false);
  const hasData = !data.loading && !!data.data;
  const isNewDesign = dataService.isNewDesign;

  const handleNextClick = () => {
    // in summary we have final validation: in case assets/liabilities present(valid) on prev step but now missing - raise error
    const assetsQuestion = questions.find(q => getUID(q.uid) === ASSET_DATA_UID);
    const liabilityQuestion = questions.find(q => getUID(q.uid) === LIABILITY_DATA_UID);
    let totalAmount = 0;

    if(assetsQuestion){
      const assets = _.flatten(Object.entries(data.data.assets).map(([categoryId, c]) => getActiveAssets(categoryId, c)));

      Object.entries(depotsAndCashData.data.assets || {}).forEach((([categoryId, c]) => c.assets.forEach(
        a => assets.push(getAsset(a, categoryId))
      )));

      Object.entries(depotsAndCashData.data.portfolios || {}).forEach((([categoryId, c]) => c.assets.forEach(
        a => assets.push(getAsset(a, categoryId))
      )));

      if(assets.length === 0){
        if(isSummary && !_.isEmpty(dataService.getStepAnswer(ASSETS_STEP_UID, ASSET_DATA_UID))){
          dispatch(displayErrorSnackBar(
            "Vermögenswerte sind unvollständig. Bitte zum vorherigen Schritt zurückkehren und die Angaben überprüfen."
          ));

          return;
        } else {
          assetsQuestion.answer = null;
        }
      } else {
        assetsQuestion.answer = assets;
        totalAmount += _.sumBy(assets, 'value');
      }
    }
    if(liabilityQuestion){
      const noLiabilities = activeLiabilities.length === 0;
      if(noLiabilities){
        if(isSummary && !_.isEmpty(dataService.getStepAnswer(LIABILITIES_V2_STEP_UID, LIABILITY_DATA_UID))){
          dispatch(displayErrorSnackBar(
            "Verbindlichkeiten sind unvollständig. Bitte zum vorherigen Schritt zurückkehren und die Angaben überprüfen."
          ));

          return;
        } else {
          liabilityQuestion.answer = null;
        }
      } else {
        liabilityQuestion.answer = activeLiabilities;
        totalAmount -= _.sumBy(activeLiabilities, 'value');
      }

      if(liabilityCheckbox){
        dataService.setLiabilityCheckboxRequired(liabilityCheckbox, noLiabilities);
      }
    }
    if(isSummary){
      const totalQuestion = questions.find(q => getUID(q.uid) === 'M0');
      totalQuestion.answer = totalAmount;
    }
    onNextClick().then(() => {
      const errors = _.compact(_.map(questions, 'error'));
      if(errors.length){
        dispatch(displayErrorSnackBar([...new Set(errors)].join('\n')))
      }
    });
  };

  const stepUID = React.useMemo(() => dataService._getStepUID(uid), []);
  const liabilityCheckbox = React.useMemo(() => questions.find(q => getUID(q.uid) === LIABILITY_CHECKBOX_UID), []);
  const activeLiabilities = React.useMemo(() =>
    hasData
      ? _.flatten(Object.entries(data.data.liabilities).map(([categoryId, c]) => getActiveAssets(categoryId, c)))
      : [], [data.updatedAt]);

  const retry = () => {
    if(data.error) {
      fetch();
    }
    if(depotsAndCashData.error) {
      fetchDepotsAndCash();
    }
  };

  const onUpdate = (itemType, type, categoryId, item) => {
    dispatchAssetsAndLiabilities({type, itemType, categoryId, item});
  };

  const isSummary = stepUID === SUMMARY_STEP_UID;
  const AssetListComponent = stepUID === ASSETS_STEP_UID ? AssetsList : LiabilitiesList;
  if(liabilityCheckbox){
    const hasActiveLiabilities = activeLiabilities.length > 0;
    dataService.setLiabilityCheckboxRequired(liabilityCheckbox, !hasActiveLiabilities);
  }

  const error = data.error || depotsAndCashData.error;

  return (
    <div className={classes.stepContainer}>
      <div className={classes.stepScrollContainer}>
        <div className={clsx(classes.stepContent, 'app-page-container', isNewDesign && 'newDesign')}>
          <Grid container justify={"center"} spacing={3}>
            {!!error ? (
              <div style={{textAlign: 'center'}}>
                <b>{getErrorMessage(error)}</b>
                <BcaIconButton
                  icon={<i className={'fa fa-refresh'} />}
                  tooltip={'Erneut versuchen'}
                  onClick={retry}
                />
              </div>
            ) : isSummary ? (
              <Grid item xs={12}>
                <Summary
                  assetsAndLiabilitiesData={data}
                  depotsAndCashData={depotsAndCashData}
                  stepName={name}
                  extra_texts={extra_texts}
                  showNewBadge
                />
              </Grid>
            ) : (
              <Grid item xs={12} lg={10}>
                <AssetListComponent
                  data={data}
                  depotsAndCashData={depotsAndCashData}
                  onUpdate={onUpdate}
                  onDepotsUpdate={removeDepot}
                  customerId={dataService.customer_id}
                  name={name}
                  helpText={help_text}
                  showNewBadge
                >
                  {hasData && !!liabilityCheckbox && getLiabilityCheckboxRequired(liabilityCheckbox, isNewDesign) && (
                    <Grid container justify={"center"}>
                      <Grid item xs={7} key={liabilityCheckbox.uid}>
                        <CheckboxCardQuestion
                          question={liabilityCheckbox}
                          onAnswerChange={onAnswerChange}
                        />
                      </Grid>
                    </Grid>
                  )}
                </AssetListComponent>
              </Grid>
            )}
          </Grid>
        </div>
        <div className={classes.scrollFix} />
      </div>

      <StepFooter
        leftItems={isNewDesign ? undefined : [
          <PrimaryButton
            text={"Zurück"}
            icon={<ArrowBack color={'primary'}/>}
            variant={"outlined"}
            onButtonClick={onPrevClick}
          />
        ]}
        rightItems={[
          ...extraRightButtons,
          <PrimaryButton
            text={SAVE_AND_CONTINUE_BTN_LABEL}
            onButtonClick={handleNextClick}
            disabled={!hasData || depotsAndCashData.loading || !!error}
          />
        ]}
      />
    </div>
  );
};

AssetsV2Step.defaultProps = {
  extraRightButtons: []
};

export default connect()(withStyles(styles)(AssetsV2Step));
